import { Project, SyntaxKind, ObjectLiteralExpression, ObjectLiteralElementLike, PropertyAssignment, VariableDeclaration, IndentationText } from "ts-morph";
import * as path from "path";
import * as fs from "fs";

export class AstSchemaEditor {
    private project: Project;
    private collectionsDir: string;

    constructor(collectionsDir: string) {
        this.project = new Project({
            manipulationSettings: {
                indentationText: IndentationText.FourSpaces
            }
        });
        if (fs.existsSync(collectionsDir)) {
            this.project.addSourceFilesAtPaths(`${collectionsDir}/**/*.ts`);
        }
        this.collectionsDir = path.resolve(collectionsDir);
    }

    /**
     * Sanitize collectionId to prevent path traversal attacks.
     * Only allows alphanumeric characters, underscores, and hyphens.
     */
    private sanitizeCollectionId(collectionId: string): string {
        const sanitized = collectionId.replace(/[^a-zA-Z0-9_-]/g, "");
        if (!sanitized || sanitized !== collectionId) {
            throw new Error(`Invalid collection ID: "${collectionId}". Only alphanumeric characters, underscores, and hyphens are allowed.`);
        }
        return sanitized;
    }

    /**
     * Resolve a file path and ensure it falls within the collectionsDir.
     */
    private safePath(filename: string): string {
        const resolved = path.resolve(this.collectionsDir, filename);
        if (!resolved.startsWith(this.collectionsDir + path.sep) && resolved !== this.collectionsDir) {
            throw new Error("Path traversal detected: resolved path is outside the collections directory.");
        }
        return resolved;
    }

    private getCollectionFile(collectionId: string) {
        const safeId = this.sanitizeCollectionId(collectionId);
        const filePath = this.safePath(`${safeId}.ts`);
        let file = this.project.getSourceFile(filePath);
        if (!file && fs.existsSync(filePath)) {
            this.project.addSourceFilesAtPaths(`${this.collectionsDir}/**/*.ts`);
            file = this.project.getSourceFile(filePath);
        }
        return file;
    }

    private getCollectionObject(collectionId: string): ObjectLiteralExpression | null {
        const file = this.getCollectionFile(collectionId);
        if (!file) return null;

        const defaultExport = file.getDefaultExportSymbol();
        if (defaultExport) {
            const declaration = defaultExport.getDeclarations()[0];
            if (declaration && declaration.getKind() === SyntaxKind.ExportAssignment) {
                const expr = declaration.asKind(SyntaxKind.ExportAssignment)?.getExpression();
                if (expr && expr.getKind() === SyntaxKind.Identifier) {
                    const varName = expr.getText();
                    const varDecl = file.getVariableDeclaration(varName);
                    return varDecl?.getInitializerIfKind(SyntaxKind.ObjectLiteralExpression) || null;
                }
            }
        }
        // Fallback: Just get the first exported VariableDeclaration with an ObjectLiteral
        const varDecls = file.getVariableDeclarations();
        for (const varDecl of varDecls) {
            const init = varDecl.getInitializerIfKind(SyntaxKind.ObjectLiteralExpression);
            if (init) return init;
        }
        return null;
    }

    private convertJsonToAstString(obj: unknown, indentLevel = 0, oldAstNode?: ObjectLiteralExpression): string {
        // Base TS-morph parses arrays as 2 levels deep from the property key:
        // PropertiesObject = level 1, PropertyConfig = level 2.
        // We calibrate the spacing multiples to keep the items flush with standard TS format.
        const indentStr = "    ";
        const indent = indentStr.repeat(indentLevel);
        const innerIndent = indentStr.repeat(indentLevel + 1);

        if (obj === null || obj === undefined) {
            return "undefined";
        }
        if (typeof obj === "string") {
            return JSON.stringify(obj);
        }
        if (typeof obj === "number" || typeof obj === "boolean") {
            return String(obj);
        }
        if (Array.isArray(obj)) {
            if (obj.length === 0) return "[]";
            const items = obj.map(item => this.convertJsonToAstString(item, indentLevel + 1));
            return `[\n${innerIndent}${items.join(`,\n${innerIndent}`)}\n${indent}]`;
        }
        if (typeof obj === "object") {
            const record = obj as Record<string, unknown>;
            const keys = Object.keys(record);

            // Collect preserved AST properties
            const preservedProps: string[] = [];
            if (oldAstNode) {
                const oldProps = oldAstNode.getProperties();
                for (const oldProp of oldProps) {
                    if (oldProp.isKind(SyntaxKind.PropertyAssignment)) {
                        const nameNode = oldProp.getNameNode();
                        let name = nameNode.getText();
                        if (name.startsWith('"') && name.endsWith('"')) name = name.slice(1, -1);
                        if (name.startsWith("'") && name.endsWith("'")) name = name.slice(1, -1);

                        // If the JSON object doesn't have this key, check if we should preserve it
                        if (!(name in record)) {
                            const init = oldProp.getInitializer();
                            if (init) {
                                const kind = init.getKind();
                                const isCode = kind === SyntaxKind.ArrowFunction ||
                                    kind === SyntaxKind.FunctionExpression ||
                                    kind === SyntaxKind.Identifier ||
                                    kind === SyntaxKind.CallExpression ||
                                    kind === SyntaxKind.JsxElement;

                                if (isCode || name === "target" || name === "callbacks" || name === "permissions" || name === "securityRules") {
                                    // Preserve this property exactly as it was
                                    const keyStr = /^[A-Za-z_$][A-Za-z0-9_$]*$/.test(name) ? name : JSON.stringify(name);
                                    preservedProps.push(`${keyStr}: ${init.getText()}`);
                                }
                            }
                        }
                    }
                }
            }

            if (keys.length === 0 && preservedProps.length === 0) return "{}";

            const props = keys.map(key => {
                const keyStr = /^[A-Za-z_$][A-Za-z0-9_$]*$/.test(key) ? key : JSON.stringify(key);

                // If the value is an object, pass the old AST node to recurse
                let childAstNode: ObjectLiteralExpression | undefined;
                if (oldAstNode && typeof record[key] === "object" && record[key] !== null && !Array.isArray(record[key])) {
                    const oldProp = oldAstNode.getProperty(
                        (p: ObjectLiteralElementLike) => "getName" in p && typeof (p as PropertyAssignment).getName === "function" && ((p as PropertyAssignment).getName() === key || (p as PropertyAssignment).getName() === `"${key}"` || (p as PropertyAssignment).getName() === `'${key}'`)
                    );
                    if (oldProp && oldProp.isKind(SyntaxKind.PropertyAssignment)) {
                        childAstNode = oldProp.getInitializerIfKind(SyntaxKind.ObjectLiteralExpression);
                    }
                }

                return `${keyStr}: ${this.convertJsonToAstString(record[key], indentLevel + 1, childAstNode)}`;
            });

            const allProps = [...props, ...preservedProps];
            return `{\n${innerIndent}${allProps.join(`,\n${innerIndent}`)}\n${indent}}`;
        }
        return "undefined";
    }

    public async saveProperty(collectionId: string, propertyKey: string, propertyConfig: Record<string, unknown>) {
        const collectionObj = this.getCollectionObject(collectionId);
        if (!collectionObj) throw new Error(`Collection ${collectionId} not found in ATS workspace.`);

        let propertiesProp = collectionObj.getProperty("properties") as PropertyAssignment;
        if (!propertiesProp) {
            propertiesProp = collectionObj.addPropertyAssignment({
                name: "properties",
                initializer: "{}"
            });
        }

        const propsObj = propertiesProp.getInitializerIfKind(SyntaxKind.ObjectLiteralExpression);
        if (propsObj) {
            const existingProp = propsObj.getProperty(
                (p: ObjectLiteralElementLike) => "getName" in p && typeof (p as PropertyAssignment).getName === "function" && ((p as PropertyAssignment).getName() === propertyKey || (p as PropertyAssignment).getName() === `"${propertyKey}"`)
            );

            let oldPropAstNode: ObjectLiteralExpression | undefined;
            if (existingProp && existingProp.isKind(SyntaxKind.PropertyAssignment)) {
                oldPropAstNode = existingProp.getInitializerIfKind(SyntaxKind.ObjectLiteralExpression);
            }

            const newInitializer = this.convertJsonToAstString(propertyConfig, 2, oldPropAstNode);

            if (existingProp) {
                if (existingProp.isKind(SyntaxKind.PropertyAssignment)) {
                    existingProp.setInitializer(newInitializer);
                }
            } else {
                propsObj.addPropertyAssignment({
                    name: /^[A-Za-z_$][A-Za-z0-9_$]*$/.test(propertyKey) ? propertyKey : JSON.stringify(propertyKey),
                    initializer: newInitializer
                });
            }

            const file = this.getCollectionFile(collectionId);
            if (file) {
                file.formatText();
            }
            await this.project.save();
        }
    }

    public async deleteProperty(collectionId: string, propertyKey: string) {
        const collectionObj = this.getCollectionObject(collectionId);
        if (!collectionObj) return;

        const propertiesProp = collectionObj.getProperty("properties") as PropertyAssignment;
        if (propertiesProp) {
            const propsObj = propertiesProp.getInitializerIfKind(SyntaxKind.ObjectLiteralExpression);
            if (propsObj) {
                const existingProp = propsObj.getProperty(
                    (p: ObjectLiteralElementLike) => "getName" in p && typeof (p as PropertyAssignment).getName === "function" && ((p as PropertyAssignment).getName() === propertyKey || (p as PropertyAssignment).getName() === `"${propertyKey}"`)
                );
                if (existingProp) {
                    existingProp.remove();
                    const file = this.getCollectionFile(collectionId);
                    if (file) {
                        file.formatText();
                    }
                    await this.project.save();
                }
            }
        }
    }

    public async saveCollection(collectionId: string, collectionData: Record<string, unknown>) {
        let file = this.getCollectionFile(collectionId);
        const collectionObj = this.getCollectionObject(collectionId);

        if (!file || !collectionObj) {
            // Create a new file
            const safeId = this.sanitizeCollectionId(collectionId);
            const newFilePath = this.safePath(`${safeId}.ts`);
            file = this.project.createSourceFile(newFilePath, `import { CollectionConfig } from "@rebasepro/types";\n\nconst ${safeId}Collection: CollectionConfig = ${this.convertJsonToAstString(collectionData)};\n\nexport default ${safeId}Collection;\n`, { overwrite: true });
        } else {
            // Update root level properties gracefully

            // Force delete securityRules if empty or undefined to handle Formex / serialization stripping
            if (!("securityRules" in collectionData) || collectionData.securityRules === undefined || (Array.isArray(collectionData.securityRules) && collectionData.securityRules.length === 0)) {
                const srProp = collectionObj.getProperty("securityRules");
                if (srProp) {
                    srProp.remove();
                }

                // If it was in collectionData as an empty array, delete it so the loop below doesn't add it back as "[]"
                // Actually, if it's "[]", omitting it entirely from the TS file achieves the same logical effect (no RLS rules)
                // and correctly triggers "unmapped policies" if the DB still has them.
                delete collectionData["securityRules"];
            }

            for (const key of Object.keys(collectionData)) {
                if (key === "relations") continue; // Kept via other AST functions or handled separately.

                const prop = collectionObj.getProperty(key) as PropertyAssignment;

                let oldAstNode: ObjectLiteralExpression | undefined;
                if (prop && prop.isKind(SyntaxKind.PropertyAssignment)) {
                    oldAstNode = prop.getInitializerIfKind(SyntaxKind.ObjectLiteralExpression);
                }

                const newInit = this.convertJsonToAstString(collectionData[key], 1, oldAstNode);
                if (prop) {
                    prop.setInitializer(newInit);
                } else {
                    collectionObj.addPropertyAssignment({
                        name: key,
                        initializer: newInit
                    });
                }
            }
        }
        if (file) {
            file.formatText();
        }
        await this.project.save();
    }

    public async deleteCollection(collectionId: string) {
        const file = this.getCollectionFile(collectionId);
        if (file) {
            file.deleteImmediatelySync();
        }
    }
}
